/**
 * Every workspace is provisioned with its default boards server-side. They
 * are ordinary cards here, though only a workspace admin manages one.
 *
 * The hub lists every workspace of the organization. The selected workspace's
 * own list still runs: it provisions that workspace's default boards, and the
 * cards the hub's actions change are read from it, so the organization list
 * only adds the other workspaces' trackers.
 */
import { useMemo, useState, type ChangeEvent, type RefObject } from 'react';
import { useNavigate } from 'react-router';
import {
  Button,
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  Input,
  Label,
  Textarea,
} from '@monksflow/monks-ui';
import { useQuery } from '@tanstack/react-query';
import { Check, LayoutTemplate, Plus, TriangleAlertIcon } from 'lucide-react';
import {
  boardTypeOf,
  isBriefsBoardExistsError,
  organizationTrackersQueryOptions,
  trackersListQueryOptions,
  TRACKER_ITEM_SOURCES,
  TRACKER_ITEM_SOURCE_GROUPS,
  useCreateTrackerMutation,
  useDeleteTrackerMutation,
  useDuplicateTrackerMutation,
  type TrackerItemSourceOption,
} from '@/entities/tracker';
import { useAuth, useCurrentOrganizationWorkspaces } from '@/entities/user';
import { getBuiltinTemplate } from '@/entities/tracker-template';
import { useCurrentWorkspaceId } from '@/entities/workspace';
import { notificationService } from '@/lib/services/notification.service';
import { track } from '@/shared/analytics';
import { StaggerItem } from '@/shared/ui/animations';
import {
  ListPageBody,
  ListPageLayout,
  PageHeader,
  PageHeaderIdentity,
  PageHeaderTitle,
} from '@/shared/ui/layout';
import { AboutPageDialog, EmptyState } from '@/shared/ui';
import { ROUTES } from '@/shared/config/routes';
import { EmptyStateTitle, type SortChipOption } from '@/shared/ui';
import { FormDialog } from '@/shared/ui/FormDialog';
import { EditTrackerSheet } from './components/EditTrackerSheet';
import { TrackerCard } from './components/TrackerCard';
import { TrackersHubEmptyState } from './components/TrackersHubEmptyState';
import { TrackersToolbar } from './components/TrackersToolbar';
import { trackersPageDescription, trackersPageTitle } from './constants/trackers.constants';
import { filterTrackers, type HubTracker, type TrackerOwnership } from './lib/filterTrackers';
import { TemplateGalleryDialog } from './TemplateGalleryDialog';
import { TrackerCardSkeleton } from './TrackerCardSkeleton';
import { Trans, useTranslation } from 'react-i18next';

type TrackerItemSourceChoice = TrackerItemSourceOption['id'];

/**
 * The board a new tracker of this type opens on: its grouping, and the columns
 * its starter template carries. Record-backed types come pre-columned so the
 * board is useful before anyone configures it.
 */
function startingBoard(choice: TrackerItemSourceChoice) {
  const option = TRACKER_ITEM_SOURCES.find(({ id }) => id === choice);
  const starter = option?.starterTemplateKey
    ? getBuiltinTemplate(option.starterTemplateKey)
    : undefined;
  return { groupBy: starter?.groupBy ?? option?.defaultGroupBy, layout: starter?.layout };
}

const ITEM_TYPE_GROUPS = TRACKER_ITEM_SOURCE_GROUPS.map((group) => ({
  ...group,
  options: TRACKER_ITEM_SOURCES.filter((option) => option.group === group.id),
}));

export function Trackers() {
  const { t } = useTranslation('trackers');

  const sortChoices = useMemo<ReadonlyArray<SortChipOption>>(
    () => [
      { id: 'createdAt-desc', label: t('trackers.newestFirst'), direction: 'desc' },
      { id: 'createdAt-asc', label: t('trackers.oldestFirst'), direction: 'asc' },
      { id: 'name-asc', label: t('trackers.nameAZ'), direction: 'asc' },
      { id: 'name-desc', label: t('trackers.nameZA'), direction: 'desc' },
    ],
    [t],
  );

  const navigate = useNavigate();
  const workspaceId = useCurrentWorkspaceId() ?? '';

  const trackersQuery = useQuery(trackersListQueryOptions(workspaceId));
  const organizationQuery = useQuery(organizationTrackersQueryOptions());
  const organizationWorkspaces = useCurrentOrganizationWorkspaces();
  const createTracker = useCreateTrackerMutation({ workspaceId });
  const deleteTracker = useDeleteTrackerMutation({ workspaceId });
  const duplicateTracker = useDuplicateTrackerMutation({ workspaceId });

  const [searchInput, setSearchInput] = useState('');
  const [sortId, setSortId] = useState<string | null>(null);
  const [ownership, setOwnership] = useState<TrackerOwnership>('all');
  const [workspaceFilter, setWorkspaceFilter] = useState<string | null>(null);
  const currentUserId = useAuth((s) => s.signedInUser.value)?.id ?? null;
  const [aboutOpen, setAboutOpen] = useState(false);
  const [galleryOpen, setGalleryOpen] = useState(false);
  // "New tracker" first opens a chooser (template vs blank) rather than going
  // straight to the blank create form.
  const [chooserOpen, setChooserOpen] = useState(false);
  // Whether the create dialog on screen is a step of that flow. The gallery
  // always is; the create dialog can also open on its own, where backing out
  // to a chooser nobody opened would be nonsense.
  const [createCameFromChooser, setCreateCameFromChooser] = useState(false);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editOpen, setEditOpen] = useState(false);
  const [editReturnFocus, setEditReturnFocus] = useState<RefObject<HTMLElement | null>>();
  const [draftName, setDraftName] = useState('');
  const [draftDescription, setDraftDescription] = useState('');
  const [draftItemSource, setDraftItemSource] = useState<TrackerItemSourceChoice>('text');

  // Delete confirmation state.
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const hubTrackers = useMemo<HubTracker[]>(() => {
    const unnamed = t('trackers.workspaceFilter.unnamedWorkspace');
    const currentName =
      organizationWorkspaces.find((workspace) => workspace.id === workspaceId)?.name ?? unnamed;
    return [
      ...(trackersQuery.data ?? []).map((tracker) => ({
        ...tracker,
        workspaceId,
        workspaceName: currentName,
      })),
      // A failed organization list leaves the hub on the selected workspace alone.
      ...(organizationQuery.data ?? [])
        .filter((tracker) => tracker.workspaceId !== workspaceId)
        .map((tracker) => ({
          ...tracker,
          workspaceName: tracker.workspaceName ?? unnamed,
        })),
    ];
  }, [trackersQuery.data, organizationQuery.data, organizationWorkspaces, workspaceId, t]);

  const listedWorkspaces = useMemo(() => {
    const names = new Map<string, string>();
    for (const tracker of hubTrackers) {
      if (!names.has(tracker.workspaceId)) {
        names.set(tracker.workspaceId, tracker.workspaceName);
      }
    }
    return [...names].map(([id, name]) => ({ id, name }));
  }, [hubTrackers]);
  const spansWorkspaces = listedWorkspaces.length > 1;
  // A filter left on a workspace that no longer lists anything would hide every card.
  const activeWorkspaceFilter = listedWorkspaces.some(({ id }) => id === workspaceFilter)
    ? workspaceFilter
    : null;

  const cards = useMemo(
    () =>
      filterTrackers(hubTrackers, {
        ownership,
        currentUserId,
        search: searchInput,
        sortId,
        workspaceId: activeWorkspaceFilter,
      }),
    [hubTrackers, ownership, currentUserId, searchInput, sortId, activeWorkspaceFilter],
  );

  const hasBriefsBoard = (trackersQuery.data ?? []).some(
    (tracker) => tracker.itemSource === 'briefs',
  );

  const deletingTracker = deletingId
    ? (trackersQuery.data ?? []).find((t) => t.id === deletingId)
    : undefined;

  const openCreateDialog = ({ fromChooser = false } = {}) => {
    setDraftName('');
    setDraftDescription('');
    setDraftItemSource('text');
    setCreateCameFromChooser(fromChooser);
    setDialogOpen(true);
  };

  const openEditSheet = (id: string, menuTrigger: RefObject<HTMLElement | null>) => {
    setEditingId(id);
    setEditReturnFocus(menuTrigger);
    setEditOpen(true);
  };

  /** Step back to the chooser from whichever branch of it is on screen. */
  const backToChooser = () => {
    setGalleryOpen(false);
    setDialogOpen(false);
    setChooserOpen(true);
  };

  const handleSubmit = () => {
    const name = draftName.trim();
    if (!name) return;
    const description = draftDescription.trim();
    createTracker.mutate(
      {
        name,
        description: description || undefined,
        itemSource: draftItemSource,
        // A free-form board leaves both unset, so it seeds its own Custom
        // starter group and an empty column set.
        ...startingBoard(draftItemSource),
      },
      {
        onSuccess: (created) => {
          setDialogOpen(false);
          track('tracker_created', {
            tracker_id: created.id,
            board_type: boardTypeOf(created.itemSource),
            grouped: Boolean(created.groupBy),
          });
          void navigate(ROUTES.TRACKER_DETAIL(created.id));
        },
        onError: (error) =>
          notificationService.showError(
            isBriefsBoardExistsError(error)
              ? t('trackers.briefsBoardExists')
              : t('trackers.couldNotCreateTheTracker'),
          ),
      },
    );
  };

  const handleConfirmDelete = () => {
    if (!deletingId) return;
    const deleted = (trackersQuery.data ?? []).find((t) => t.id === deletingId);
    deleteTracker.mutate(
      { trackerId: deletingId },
      {
        onSuccess: () => {
          track('tracker_deleted', {
            tracker_id: deletingId,
            board_type: boardTypeOf(deleted?.itemSource),
          });
          setDeletingId(null);
        },
        onError: () => notificationService.showError(t('trackers.couldNotDeleteTheTracker')),
      },
    );
  };

  const handleDuplicate = (id: string) => {
    const tracker = (trackersQuery.data ?? []).find((t) => t.id === id);
    if (!tracker) return;
    duplicateTracker.mutate(tracker, {
      onSuccess: (created) => {
        track('tracker_duplicated', {
          tracker_id: created.id,
          board_type: boardTypeOf(created.itemSource),
        });
      },
      onError: () => notificationService.showError(t('trackers.couldNotDuplicateTheTracker')),
    });
  };

  const handleNewTracker = () => setChooserOpen(true);

  const hasNonSearchFilter = ownership !== 'all' || activeWorkspaceFilter !== null;
  const hasActiveFilters = searchInput.trim().length > 0 || hasNonSearchFilter;
  const hasRows = hubTrackers.length > 0;
  // With no trackers at all there's nothing to search or sort — hide the
  // toolbar and let the true-empty state own the surface (mirrors Briefing).
  const showToolbar = hasRows || trackersQuery.isPending || hasActiveFilters;
  // A failed first load gets its own state, never the create invite. A failed refetch keeps
  // its cached list, so only isLoadingError (no data yet) counts.
  const showInitialEmpty =
    !trackersQuery.isPending && !trackersQuery.isLoadingError && !hasRows && !hasActiveFilters;

  // The inset matches the toolbar's so the cards' left edge lines up with the
  // search bar; pt-0 keeps them tight under it.
  const cardGridClassName =
    'grid grid-cols-[repeat(auto-fill,minmax(320px,1fr))] gap-md px-section pb-section pt-0';

  return (
    <>
      <ListPageLayout>
        {/* Staggered entrance — same 50ms rhythm as Projects/Knowledge. */}
        <StaggerItem index={0}>
          <PageHeader>
            <PageHeaderIdentity>
              <PageHeaderTitle info={{ onClick: () => setAboutOpen(true) }}>
                {trackersPageTitle()}
              </PageHeaderTitle>
            </PageHeaderIdentity>
          </PageHeader>
        </StaggerItem>

        <ListPageBody>
          {/* Projects-style toolbar: search + sort left, primary action right. */}
          {showToolbar && (
            <StaggerItem index={2}>
              <TrackersToolbar
                search={searchInput}
                onSearchChange={setSearchInput}
                sortOptions={sortChoices}
                sortId={sortId}
                onSortChange={setSortId}
                ownership={ownership}
                onOwnershipChange={setOwnership}
                workspaces={listedWorkspaces}
                workspaceId={activeWorkspaceFilter}
                onWorkspaceChange={setWorkspaceFilter}
                onNewTracker={handleNewTracker}
              />
            </StaggerItem>
          )}

          <StaggerItem index={3} className="flex min-h-0 flex-1 flex-col">
            {trackersQuery.isPending ? (
              <div data-testid="trackers-card-grid-skeleton" className={cardGridClassName}>
                {Array.from({ length: 6 }).map((_, i) => (
                  <TrackerCardSkeleton key={i} />
                ))}
              </div>
            ) : showInitialEmpty ? (
              <TrackersHubEmptyState
                workspaceId={workspaceId}
                onStartFromScratch={() => openCreateDialog()}
              />
            ) : trackersQuery.isLoadingError && !hasRows ? (
              <EmptyState
                role="alert"
                variant="outline-dashed"
                className="mx-section mb-section flex-1"
                icon={<TriangleAlertIcon aria-hidden />}
                title={t('trackers.couldNotLoadTrackers')}
                description={t('trackers.couldNotLoadTrackersDescription')}
              >
                <Button variant="outline" onClick={() => void trackersQuery.refetch()}>
                  {t('trackers.tryAgain')}
                </Button>
              </EmptyState>
            ) : cards.length === 0 ? (
              <Empty role="status" data-testid="trackers-no-results-state" className="flex-1">
                <EmptyHeader>
                  <EmptyStateTitle>{t('trackers.noMatchingTrackers')}</EmptyStateTitle>
                  <EmptyDescription>
                    {hasNonSearchFilter
                      ? t('trackers.noTrackersMatchThisFilter')
                      : t('trackers.tryADifferentSearch')}
                  </EmptyDescription>
                </EmptyHeader>
                <EmptyContent>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setSearchInput('');
                      setOwnership('all');
                      setWorkspaceFilter(null);
                    }}
                  >
                    {hasNonSearchFilter ? t('trackers.showAllTrackers') : t('trackers.clearSearch')}
                  </Button>
                </EmptyContent>
              </Empty>
            ) : (
              <div data-testid="trackers-card-grid" className={cardGridClassName}>
                {cards.map((tracker) => (
                  <TrackerCard
                    key={tracker.id}
                    tracker={tracker}
                    workspaceName={spansWorkspaces ? tracker.workspaceName : undefined}
                    inCurrentWorkspace={tracker.workspaceId === workspaceId}
                    onEdit={openEditSheet}
                    onDelete={setDeletingId}
                    onDuplicate={handleDuplicate}
                  />
                ))}
              </div>
            )}
          </StaggerItem>
        </ListPageBody>
      </ListPageLayout>

      {/* The design gives Trackers no rows, so the description the 56px header
          dropped is the whole dialog. */}
      <AboutPageDialog
        open={aboutOpen}
        onOpenChange={setAboutOpen}
        title={t('trackers.about.title')}
        description={trackersPageDescription()}
        rows={[]}
      />

      {/* New tracker: choose template vs blank, then route to the right modal. */}
      <FormDialog open={chooserOpen} onOpenChange={setChooserOpen} title={t('trackers.newTracker')}>
        <FormDialog.Body>
          <div className="flex flex-col gap-2">
            <button
              type="button"
              onClick={() => {
                setChooserOpen(false);
                setGalleryOpen(true);
              }}
              className="flex items-start gap-3 rounded-lg border border-border bg-background p-4 text-left transition-colors hover:border-primary hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <LayoutTemplate
                className="mt-0.5 size-5 shrink-0 text-muted-foreground"
                aria-hidden="true"
              />
              <span className="flex flex-col gap-0.5">
                <span className="text-sm font-medium text-foreground">
                  {t('trackers.startFromATemplate')}
                </span>
                <span className="text-xs text-muted-foreground">
                  {t('trackers.pickAReadyMadeTracker')}
                </span>
              </span>
            </button>
            <button
              type="button"
              onClick={() => {
                setChooserOpen(false);
                openCreateDialog({ fromChooser: true });
              }}
              className="flex items-start gap-3 rounded-lg border border-border bg-background p-4 text-left transition-colors hover:border-primary hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <Plus className="mt-0.5 size-5 shrink-0 text-muted-foreground" aria-hidden="true" />
              <span className="flex flex-col gap-0.5">
                <span className="text-sm font-medium text-foreground">
                  {t('trackers.startBlank')}
                </span>
                <span className="text-xs text-muted-foreground">
                  {t('trackers.createAnEmptyTrackerAnd')}
                </span>
              </span>
            </button>
          </div>
        </FormDialog.Body>
        <FormDialog.Footer
          secondary={{ label: t('trackers.cancel'), onClick: () => setChooserOpen(false) }}
        />
      </FormDialog>

      <EditTrackerSheet
        tracker={(trackersQuery.data ?? []).find((tracker) => tracker.id === editingId)}
        open={editOpen}
        onOpenChange={setEditOpen}
        returnFocusTo={editReturnFocus}
      />

      <TemplateGalleryDialog
        open={galleryOpen}
        onOpenChange={setGalleryOpen}
        onBack={backToChooser}
        workspaceId={workspaceId}
      />

      <FormDialog open={dialogOpen} onOpenChange={setDialogOpen} title={t('trackers.newTracker')}>
        <FormDialog.Body>
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="tracker-name" className="text-foreground">
                {t('trackers.name')}
              </Label>
              <Input
                id="tracker-name"
                // eslint-disable-next-line jsx-a11y/no-autofocus -- first field of a modal the user just opened to type into
                autoFocus
                placeholder={t('trackers.eGCampaignLaunches')}
                value={draftName}
                maxLength={120}
                onChange={(e: ChangeEvent<HTMLInputElement>) => setDraftName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleSubmit();
                }}
              />
            </div>
            {/* Item type is picked once here and can't change later — switching
               source would strand a tracker's columns/values. */}
            <div className="flex flex-col gap-3">
              <span className="text-sm font-medium text-foreground">{t('trackers.itemType')}</span>
              {/* Grouped because the four types are not peers: the heading
                 carries what the record-backed rows have in common, so each
                 row's own description only has to say how it differs. */}
              {ITEM_TYPE_GROUPS.map((group) => (
                <div
                  key={group.id}
                  role="group"
                  aria-label={t(group.labelKey)}
                  className="flex flex-col gap-2"
                >
                  <span className="text-xs font-medium text-muted-foreground">
                    {t(group.labelKey)}
                  </span>
                  {group.options.map(({ id, labelKey, descriptionKey, Icon }) => {
                    const selected = draftItemSource === id;
                    // The server keeps one briefs board per workspace.
                    const taken = id === 'briefs' && hasBriefsBoard;
                    return (
                      <button
                        key={id}
                        type="button"
                        aria-pressed={selected}
                        disabled={taken}
                        onClick={() => setDraftItemSource(id)}
                        className={`flex items-start gap-3 rounded-lg border p-3 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:bg-muted ${
                          selected
                            ? 'border-primary bg-accent'
                            : 'border-border bg-background enabled:hover:border-primary enabled:hover:bg-accent'
                        }`}
                      >
                        <Icon
                          className="mt-0.5 size-5 shrink-0 text-muted-foreground"
                          aria-hidden="true"
                        />
                        <span className="flex flex-1 flex-col gap-0.5">
                          <span className="text-sm font-medium text-foreground">{t(labelKey)}</span>
                          <span className="text-xs text-muted-foreground">
                            {taken ? t('trackers.briefsBoardExists') : t(descriptionKey)}
                          </span>
                        </span>
                        {selected ? (
                          <Check
                            className="mt-0.5 size-4 shrink-0 text-foreground"
                            aria-hidden="true"
                          />
                        ) : null}
                      </button>
                    );
                  })}
                </div>
              ))}
            </div>
            <div className="flex flex-col gap-1.5">
              <div className="flex items-center gap-1">
                <Label htmlFor="tracker-description" className="text-foreground">
                  {t('trackers.description')}
                </Label>
                <span className="text-sm font-normal text-muted-foreground">
                  {t('trackers.optional')}
                </span>
              </div>
              <Textarea
                id="tracker-description"
                placeholder={t('trackers.whatWillThisTrackerKeep')}
                value={draftDescription}
                maxLength={500}
                rows={3}
                onChange={(e) => setDraftDescription(e.target.value)}
              />
            </div>
          </div>
        </FormDialog.Body>
        <FormDialog.Footer
          primary={{
            label: t('trackers.createTracker'),
            onClick: handleSubmit,
            disabled: draftName.trim().length === 0,
            loading: createTracker.isPending,
          }}
          secondary={
            createCameFromChooser
              ? { label: t('trackers.back'), onClick: backToChooser }
              : { label: t('trackers.cancel'), onClick: () => setDialogOpen(false) }
          }
        />
      </FormDialog>

      <FormDialog
        open={deletingId !== null}
        onOpenChange={(next) => {
          if (!next) setDeletingId(null);
        }}
        title={t('trackers.deleteTracker')}
      >
        <FormDialog.Body>
          <p className="text-sm text-foreground">
            <Trans
              i18nKey="trackers.deleteTrackerConfirmation"
              ns="trackers"
              values={{ trackerName: deletingTracker?.name ?? t('trackers.thisTracker') }}
              components={{ name: <span className="font-semibold" /> }}
            />
          </p>
        </FormDialog.Body>
        <FormDialog.Footer
          primary={{
            label: t('trackers.delete'),
            onClick: handleConfirmDelete,
            loading: deleteTracker.isPending,
          }}
          secondary={{ label: t('trackers.cancel'), onClick: () => setDeletingId(null) }}
        />
      </FormDialog>
    </>
  );
}
